/* eslint-disable @typescript-eslint/no-namespace */
// using ensite form with general form
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { findEventByPostalCode, checkPostalCodeExists, checkPostalCodeExistsEnsite, listAllPostalCodes, type Event } from '../lib/api/events';
import BookingForm from '../components/BookingForm';
import BookingFormEnsite from '../components/BookingFormEnsite';
import BookingFormGeneral from '../components/BookingFormGeneral';
import { supabase } from '@/lib/supabase';

interface AddressDetails {
  formatted_address: string;
  street_address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface SiteSurvey3Props {
  projectId?: string;
  projectAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
}


declare global {
  interface Window {
    google: any;
  }
}

namespace google.maps {
  export interface Map {
    setCenter(latLng: {lat: number, lng: number}): void;
    setZoom(zoom: number): void;
  }
  export interface Marker {
    setMap(map: Map | null): void;
  }
}

const normalizeStreetAddress = (street: string): string => {
  return street
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove punctuation
    .replace(/\s+/g, ' ') // Replace multiple spaces with a single space
    .trim();
};

const ENSITE_EVENT: Event = {
  id: '00000000-0000-0000-0000-000000000000', // Use a fixed UUID for ensite
  name: 'Ensite Survey',
  slug: 'ensite-survey',
  duration: 120,
  meeting_type: 'site-survey',
};



// utils/loadGoogleMapsApi.ts
export const loadGoogleMapsApi = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    const existingScript = document.getElementById('googleMapsScript');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      return;
    }

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const script = document.createElement('script');
    script.id = 'googleMapsScript';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.body.appendChild(script);
  });
};



export default function SiteSurveyV3({ projectId, projectAddress }: SiteSurvey3Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [address, setAddress] = useState<AddressDetails | null>(null);
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [matchedEvent, setMatchedEvent] = useState<Event | null>(null);
  const [generalMatchedEvent, setGeneralMatchedEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showEnsiteForm, setShowEnsiteForm] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  const [step, setStep] = useState(1);
  const [postalCode, setPostalCode] = useState('');
  const [availablePostalCodes, setAvailablePostalCodes] = useState<string[]>([]);
  const [showPostalCodeList, setShowPostalCodeList] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const [addressVerified, setAddressVerified] = useState<boolean>(false);
  const [projectData, setProjectData] = useState<any>(null);
  const [salesRepData, setSalesRepData] = useState<any>(null);


 
  useEffect(() => {
    // Fetch all available postal codes when component mounts
    const fetchPostalCodes = async () => {
      try {
        const codes = await listAllPostalCodes();
        setAvailablePostalCodes(codes);
      } catch (error) {
        console.error('Error fetching postal codes:', error);
      }
    };

    fetchPostalCodes();
  }, []);

  // Fetch project and sales rep data when projectId is available
  useEffect(() => {
    const fetchProjectData = async () => {
      if (!projectId) return;

      try {
        // Fetch project data
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .single();

        if (projectError) {
          console.error('Error fetching project:', projectError);
          return;
        }

        setProjectData(project);

        // Fetch sales rep data if we have an assigned_sales_rep_id
        if (project?.assigned_sales_rep_id) {
          const { data: salesRep, error: salesRepError } = await supabase
            .from('users')
            .select('first_name, last_name')
            .eq('id', project.assigned_sales_rep_id)
            .single();

          if (salesRepError) {
            console.error('Error fetching sales rep:', salesRepError);
            return;
          }

          setSalesRepData(salesRep);
        }
      } catch (error) {
        console.error('Error fetching project data:', error);
      }
    };

    fetchProjectData();
  }, [projectId]);

  useEffect(() => {
    const addressFromParams = {
      formatted_address: searchParams.get('address') || '',
      street_address: searchParams.get('street_address') || '',
      city: searchParams.get('city') || '',
      state: searchParams.get('state') || '',
      postal_code: searchParams.get('postal_code') || '',
      country: searchParams.get('country') || '',
    };

    const lat = parseFloat(searchParams.get('latitude') || '0');
    const lng = parseFloat(searchParams.get('longitude') || '0');

    if (addressFromParams.formatted_address) {
      setAddress(addressFromParams);
      setCoordinates({ lat, lng });
      // setAddressSelected(true);
      if (addressFromParams.postal_code) {
        setPostalCode(addressFromParams.postal_code);
        findEventByPostalCodeOnly(addressFromParams.postal_code);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const initMap = async () => {
      if (mapRef.current && !map) {
        try {
          await loadGoogleMapsApi(); // wait for the script to load
        } catch (error) {
          console.error('Failed to load Google Maps API:', error);
        }
        const initialMap = new window.google.maps.Map(mapRef.current, {
          center: coordinates || { lat: 37.0902, lng: -95.7129 },
          zoom: 19,
          mapTypeId: window.google.maps.MapTypeId.SATELLITE,
          mapTypeControl: false,
          streetViewControl: false,
          rotateControl: false,
          scaleControl: false,
          zoomControl: false,
          fullscreenControl: false,
          gestureHandling: 'none',
          keyboardShortcuts: false,
          clickableIcons: false,
          disableDefaultUI: true,
          styles: [
            {
              featureType: 'all',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'transit',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'road',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'administrative',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });
        setMap(initialMap);

        if (coordinates) {
          new window.google.maps.Marker({
            position: coordinates,
            map: initialMap,
            animation: window.google.maps.Animation.DROP
          });
        }
      }
    }
    initMap();
  }, [map, coordinates]);

  // Pre-fill the input when projectAddress is provided
  useEffect(() => {
    if (projectAddress && autocompleteInputRef.current && !address) {
      const fullAddress = `${projectAddress.street || ''} ${projectAddress.city || ''} ${projectAddress.state || ''} ${projectAddress.zipCode || ''}`.trim();
      if (fullAddress) {
        autocompleteInputRef.current.value = fullAddress;
      }
    }
  }, [projectAddress, address]);

  useEffect(() => {
    if (autocompleteInputRef.current && map) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        autocompleteInputRef.current,
        // { types: ['address'], componentRestrictions: { country: 'US' } }
        { types: ['address']}
      );

      autocomplete.addListener('place_changed', async () => {
        const place = autocomplete.getPlace();
        
        if (!place.geometry) {
          toast.error('Please select a valid address from the dropdown');
          return;
        }
              
        setAddressVerified(true);
        let streetNumber = '';
        let route = '';
        let city = '';
        let state = '';
        let postalCode = '';
        let country = '';

        for (const component of place.address_components) {
          const types = component.types;
          
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          if (types.includes('route')) {
            route = component.long_name;
          }
          if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('sublocality') || types.includes('sublocality_level_1') || types.includes('neighborhood')) {
            // Fallback to sublocality or neighborhood if locality is missing
            if (!city) city = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            state = component.short_name;
          }
          if (types.includes('postal_code')) {
            postalCode = component.long_name;
          }
          if (types.includes('country')) {
            country = component.long_name;
          }
        }

        
        if (!city && place.formatted_address) {
          const addressParts = place.formatted_address.split(', ');
          if (addressParts.length >= 3) {
            city = addressParts[addressParts.length - 3] || '';
          }
        }

        const addressDetails: AddressDetails = {
          formatted_address: place.formatted_address,
          street_address: `${streetNumber} ${route}`.trim(),
          city,
          state,
          postal_code: postalCode,
          country
        };

        setAddress(addressDetails);
        setSelectedState(state);
        setPostalCode(postalCode);
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setCoordinates({ lat, lng });

        const params = new URLSearchParams(searchParams); // Start with existing parameters
        
        // Update only the address-related parameters
        params.set('address', place.formatted_address);
        params.set('street_address', addressDetails.street_address);
        params.set('city', addressDetails.city);
        params.set('state', addressDetails.state);
        params.set('postal_code', addressDetails.postal_code);
        params.set('country', addressDetails.country);
        params.set('latitude', lat.toString());
        params.set('longitude', lng.toString());
        
        const currentPath = window.location.pathname;
        navigate(`${currentPath}?${params.toString()}`, { replace: true });


        map.setCenter({ lat, lng });
        map.setZoom(19);

        if (marker) {
          marker.setMap(null);
        }
        const newMarker = new window.google.maps.Marker({
          position: { lat, lng },
          map: map,
          animation: window.google.maps.Animation.DROP
        });
        setMarker(newMarker);



        // First try to find an event by postal code
        await findEventByPostalCodeOnly(postalCode);
        setAddressSelected(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, marker, searchParams]);

  const findEventByPostalCodeOnly = async (postalCode: string) => {
    try {
      setIsLoading(true);
      // Check if postal code exists first
      const exists = await checkPostalCodeExists(postalCode);
      const ensiteExists = await checkPostalCodeExistsEnsite(postalCode);
      if (!ensiteExists && !exists) {
        // If no event found by postal code, show Ensite form
        const event = await findEventByPostalCode("0000000000");
        if (event) {
          // Found an event with matching postal code
          setMatchedEvent(event);
          setStep(2);
          setIsLoading(false);
          return true;
        }
      }

      if (!exists) {
        // If no event found by postal code, show Ensite form
        setMatchedEvent(ENSITE_EVENT);
        setIsLoading(false);
        return false;
      }
      
      // Try to find an event by postal code
      const event = await findEventByPostalCode(postalCode);
      
      if (event) {
        // Found an event with matching postal code
        if (event.event_type == "general"){
          setGeneralMatchedEvent(event)          
        }
        else{
          setMatchedEvent(event);
        }        
        setStep(2);
        setIsLoading(false);
        return true;
      }
      
      // Fallback to Ensite form
      setMatchedEvent(ENSITE_EVENT);
      setIsLoading(false);
      return false;
    } catch (error) {
      console.error('Error finding event by postal code:', error);
      setIsLoading(false);
      
      // Fallback to Ensite form on error
      setMatchedEvent(ENSITE_EVENT);
      return false;
    }
  };

  const handleProceedToBooking = () => {
    setShowEnsiteForm(false);
    setShowBookingForm(true);
  };

  const handleProceedToBookingEnsite = () => {
    setShowBookingForm(false);
    setShowEnsiteForm(true);
  };


  // Function to get a human-readable meeting type
  const getMeetingTypeLabel = (type: string) => {
    switch (type) {
      case 'site-survey':
        return 'Site Survey';
      case 'site-installation':
        return 'Site Installation';
      default:
        return type;
    }
  };

  // Create URLSearchParams with customer and sales rep data
  const createBookingParams = () => {
    const params = new URLSearchParams(searchParams);

    // Add customer data from project if available
    if (projectData) {
      // Use project_name field which contains "FirstName LastName"
      if (projectData.project_name) {
        const nameParts = projectData.project_name.split(' ');
        if (nameParts.length >= 2) {
          params.set('firstName', nameParts[0]);
          params.set('lastName', nameParts.slice(1).join(' '));
        } else if (nameParts.length === 1) {
          params.set('firstName', nameParts[0]);
        }
      }

      // Add contact info
      if (projectData.contact_email) {
        params.set('email', projectData.contact_email);
      }
      if (projectData.contact_phone) {
        params.set('phoneNumber', projectData.contact_phone);
      }
    }

    // Add sales rep name if available
    if (salesRepData) {
      const salesRepName = `${salesRepData.first_name || ''} ${salesRepData.last_name || ''}`.trim();
      if (salesRepName) {
        params.set('salesRepName', salesRepName);
      }
    }

    return params;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="p-6">
              <div className="flex justify-center mb-4">
                <img 
                  src="/joulescrmlogo.svg" 
                  alt="Joules CRM Logo" 
                  className="h-24 w-auto"
                />
              </div>
              <h1 className="text-3xl font-bold text-foreground text-center mb-2">
                {matchedEvent?.meeting_type === 'site-installation' ? 'Book a Site Installation' : 'Book a Site Survey'}
              </h1>
              <label htmlFor="selectedDate" className="block text-sm font-medium text-foreground mb-6 text-center">
                Availability is subject to surveyor scheduling. Enter your address to schedule a {matchedEvent?.meeting_type === 'site-installation' ? 'site installation' : 'site survey'} in areas with STRONG or GOOD coverage. If coverage appears LIGHT, we recommend selecting a time at least a week in advance to ensure a local installer or surveyor can be assigned.
              </label>
              <div className="space-y-8">
                {!showBookingForm && !showEnsiteForm ? (
                  <div>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="address" className="block text-sm font-medium text-foreground mt-4 mb-1">
                          Search by Full Address
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MapPin className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            ref={autocompleteInputRef}
                            defaultValue={address?.formatted_address || (projectAddress ? `${projectAddress.street || ''} ${projectAddress.city || ''} ${projectAddress.state || ''} ${projectAddress.zipCode || ''}`.trim() : '')}
                            className="block w-full pl-10 pr-3 py-3 border border-border rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary-500 sm:text-sm"
                            placeholder="Enter your full address"
                          />
                        </div>
                        {addressVerified && addressSelected && (
                          <>
                            <div className="mt-4 mb-4">
                              {matchedEvent?.name !== 'Ensite Survey' ? (
                                <button
                                  onClick={handleProceedToBooking}
                                  className="w-full flex justify-center items-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                >
                                  Proceed to Booking
                                </button>
                              ) : (
                                <button
                                  onClick={handleProceedToBookingEnsite}
                                  className="w-full flex justify-center items-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                >
                                  Proceed to Booking
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>

                      <div className="aspect-[3/2] rounded-lg overflow-hidden">
                        <div ref={mapRef} className="w-full h-full" />
                      </div>

                      {addressVerified && addressSelected && (
                        <div className="mt-6">
                          <div className="bg-success/5 border border-success/20 rounded-md p-4 mb-6">
                            <div className="flex">
                              <div className="flex-shrink-0">
                                <MapPin className="h-5 w-5 text-green-400" />
                              </div>
                              <div className="ml-3">
                                <h3 className="text-sm font-medium text-success">
                                  {getMeetingTypeLabel(matchedEvent?.meeting_type || 'site-survey')} Available
                                </h3>
                                <div className="mt-2 text-sm text-success">
                                  <p>We found a {getMeetingTypeLabel(matchedEvent?.meeting_type || 'site-survey').toLowerCase()} slot available in your area.</p>
                                  <p className="mt-1">{address?.formatted_address}</p>
                                </div>
                                <div className="mt-4">
                                  {matchedEvent?.name !== 'Ensite Survey' ? (
                                    <button
                                      onClick={handleProceedToBooking}
                                      className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                    >
                                      Proceed to Booking
                                    </button>
                                  ) : (
                                    <button
                                      onClick={handleProceedToBookingEnsite}
                                      className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                    >
                                      Proceed to Booking
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : showEnsiteForm && matchedEvent && address ? (
                  <div>
                    <BookingFormEnsite
                      event={matchedEvent}
                      initialAddress={{
                        address: address.formatted_address,
                        street_address: address.street_address,
                        city: address.city,
                        state: address.state,
                        postal_code: address.postal_code,
                        country: address.country,
                        latitude: coordinates?.lat || 0,
                        longitude: coordinates?.lng || 0,
                      }}
                      urlParams={createBookingParams()}
                      projectId={projectId}
                    />
                  </div>
                ) : matchedEvent && address ? (
                  <div>
                    <BookingForm
                      event={matchedEvent}
                      initialAddress={{
                        address: address.formatted_address,
                        street_address: address.street_address,
                        city: address.city,
                        state: address.state,
                        postal_code: address.postal_code,
                        country: address.country,
                        latitude: coordinates?.lat || 0,
                        longitude: coordinates?.lng || 0,
                      }}
                      urlParams={createBookingParams()}
                      projectId={projectId}
                    />
                  </div>
                ) : generalMatchedEvent && address ? (
                  <div>
                    <BookingFormGeneral
                      event={generalMatchedEvent}
                      initialAddress={{
                        address: address.formatted_address,
                        street_address: address.street_address,
                        city: address.city,
                        state: address.state,
                        postal_code: address.postal_code,
                        country: address.country,
                        latitude: coordinates?.lat || 0,
                        longitude: coordinates?.lng || 0,
                      }}
                      urlParams={createBookingParams()}
                      projectId={projectId}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}